import React, { useRef, useEffect } from "react";
import "./style.css";
import Vizualisation from "../../../../components/Vizualisation";
import useVizualisation from "../../../../helpers/useVizualisation";
import ColorLegend from "../../../../components/ColorLegend/colorLegend";
import { select } from "d3-selection";
import { scalePoint, scaleLinear, scaleOrdinal } from "d3-scale";
import { axisTop, axisBottom, axisRight, axisLeft } from "d3-axis";
import { nest } from "d3-collection";
import { line, curveCatmullRom } from "d3-shape";
import personalData from "../../data/personal.json";
import cyberData from "../../data/cyber.json";
import ecommerceData from "../../data/ecommerce.json";
import publicData from "../../data/public.json";
import privateData from "../../data/private.json";
import { Text } from "@visx/text";
import Tooltip from "./../Tooltip";

const margin = {
  top: 40,
  right: 250,
  bottom: 10,
  left: 20,
};

const DotPlot = ({
  data,
  yaxis,
  title,
  note,
  source,
  highlight,
  lowIncome,
  lowerMiddleIncome,
  upperMiddleIncome,
  highIncome,
  percent,
  indicators,
}) => {
  const { ref, width, height, windowSize, inView } = useVizualisation();

  const dataMap = {
    personal: personalData,
    cyber: cyberData,
    ecommerce: ecommerceData,
    public: publicData,
    private: privateData,
  };

  const chartData = dataMap[data];

  let plotData = chartData;
  if (yaxis == "indicator") {
    plotData = chartData.filter((d) => highlight.includes(d.indicatorCode));
  }

  const xAxisRef = useRef(null);
  const yAxisRef = useRef(null);
  const tooltipRef = useRef(null);

  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const colorScale = scaleOrdinal().range(
    ["#481F7C", "#B6A3CA", "#FCC0B4", "#EE6047"].reverse()
  );

  let pointDomain;
  let colorDomain = [...new Set(chartData.map((d) => d.income))].reverse();
  let linearScale = scaleLinear();
  let pointScale = scalePoint().padding(0.2);
  let xAxis;
  let yAxis;

  const translationsMap = {
    "Low-income": lowIncome,
    "Lower-middle-income": lowerMiddleIncome,
    "Upper-middle-income": upperMiddleIncome,
    "High-income": highIncome,
  };

  const colorScaleTranslated = scaleOrdinal()
    .range(["#481F7C", "#B6A3CA", "#FCC0B4", "#EE6047"].reverse())
    .domain(colorDomain.map((d) => translationsMap[d]));

  if (yaxis === "indicator") {
    pointDomain = [...new Set(chartData.map((d) => d.indicatorCode))];
    colorScale.domain(colorDomain);
    //horizontal linear scale, vertical point scale
    linearScale.domain([0, 100]).range([0, chartWidth]);
    pointScale.domain(pointDomain).range([0, chartHeight]);
    xAxis = axisTop(linearScale)
      .tickSize(-chartHeight)
      .tickValues([0, 25, 50, 75, 100]);
    yAxis = axisRight(pointScale).tickSize(-chartWidth).ticks(4);
    margin.bottom = 10;
    margin.left = 20;
    margin.right = 250;
  }

  const nestedByIncome = nest()
    .key((d) => d.income)
    .entries(chartData);
  const nestedByIndicator = nest()
    .key((d) => d.indicator)
    .entries(chartData);
  const lineData = yaxis == "indicator" ? nestedByIncome : nestedByIndicator;
  let lineGenerator = line().curve(curveCatmullRom.alpha(0.5));

  if (yaxis == "indicator") {
    lineGenerator.x((d) => linearScale(d.perc));
    lineGenerator.y((d) => pointScale(d.indicatorCode));
  } else {
    lineGenerator.x((d) => pointScale(d.income));
    lineGenerator.y((d) => linearScale(d.perc));
  }

  useEffect(() => {
    select(xAxisRef.current).call(xAxis);
    select(yAxisRef.current).call(yAxis);
  });

  const backgroundColorScale = scaleLinear()
    .domain([0, 100])
    .range(["#E5E5E5", "#B0CBCD"]);

  return (
    <>
      <ColorLegend colorscale={colorScaleTranslated}></ColorLegend>
      <Vizualisation
        title={title}
        note={note}
        source={source}
        ref={ref}
        size="xLarge"
      >
        <svg
          width={width}
          height={
            yaxis == "indicator"
              ? windowSize.height > 675
                ? 600
                : windowSize.height - 100
              : 350
          }
        >
          <g
            className="chart"
            transform={`translate(${margin.left}, ${margin.top})`}
          >
            <g className="g-axis">
              <g
                className={`axis axis--y ${
                  yaxis == "income" ? "axis-y-income" : ""
                }`}
                transform={`translate(${
                  yaxis == "indicator" ? chartWidth : 0
                }, 0)`}
                ref={yAxisRef}
              ></g>
              <g>
                {yaxis == "indicator" &&
                  indicators.map((d) => {
                    return (
                      <Text
                        key={"text-" + d.code}
                        x={
                          yaxis == "indicator"
                            ? linearScale(100)
                            : pointScale(d.code)
                        }
                        y={
                          yaxis == "indicator"
                            ? pointScale(d.code)
                            : linearScale(100)
                        }
                        dx={8}
                        verticalAnchor={"middle"}
                        width={240}
                        fontSize={11}
                        opacity={
                          yaxis == "indicator" && highlight.includes(d.code)
                            ? 1
                            : yaxis == "income"
                            ? 1
                            : 0.15
                        }
                        onMouseOver={() => {
                          const xOffset = margin.right - 0;
                          const bottom =
                            pointScale(d.code) > chartHeight / 2 ? true : false;
                          const yOffset = bottom ? -120 : 40;

                          tooltipRef.current.handleMouseOver(
                            d.explanation,
                            width < 770
                              ? linearScale(100) + margin.right / 2
                              : linearScale(100),
                            pointScale(d.code),
                            xOffset,
                            yOffset,
                            chartWidth,
                            true
                          );
                        }}
                        onMouseLeave={() => {
                          tooltipRef.current.handleMouseOut();
                        }}
                      >
                        {d.name + " ⓘ"}
                      </Text>
                    );
                  })}
              </g>
            </g>
            <g className="g-axis">
              <g
                className="axis axis--x"
                transform={`translate(0,${
                  yaxis == "indicator" ? 0 : chartHeight
                })`}
                ref={xAxisRef}
              ></g>
              <text
                x={
                  yaxis == "indicator"
                    ? width > 770
                      ? linearScale(50) + 8
                      : linearScale(50)
                    : -20
                }
                y={yaxis == "indicator" ? 0 : -12}
                fontSize={"10px"}
                dy={yaxis == "indicator" ? (width > 770 ? -3 : -20) : 3}
                textAnchor={
                  yaxis == "indicator"
                    ? width > 770
                      ? "start"
                      : "middle"
                    : "start"
                }
              >
                {percent}
              </text>
            </g>
            {yaxis === "indicator" && (
              <g className="background-rects">
                {xAxis
                  .tickValues()
                  .slice(0, -1)
                  .map((d) => {
                    return (
                      <rect
                        key={"rect-" + d}
                        x={linearScale(d)}
                        y={12}
                        width={linearScale(25)}
                        height={chartHeight}
                        fill={backgroundColorScale(d)}
                        opacity={0.6}
                      ></rect>
                    );
                  })}
              </g>
            )}

            {lineData.map((d) => {
              return (
                <path
                  key={"path-" + d.key}
                  d={lineGenerator(d.values)}
                  stroke={colorScale(d.key)}
                  strokeWidth={5}
                  fill={"none"}
                  opacity={0.1}
                ></path>
              );
            })}
            {plotData.map((d) => {
              return (
                <circle
                  key={`circle-${d.indicator}-${d.income}`}
                  cx={
                    yaxis == "indicator"
                      ? linearScale(d.perc)
                      : pointScale(d.income)
                  }
                  cy={
                    yaxis == "indicator"
                      ? pointScale(d.indicatorCode)
                      : linearScale(d.perc)
                  }
                  r={6}
                  fill={
                    yaxis == "indicator"
                      ? colorScale(d.income)
                      : colorScale(d.indicator)
                  }
                  stroke={"#ffffff"}
                  strokeWidth={1}
                ></circle>
              );
            })}
          </g>
        </svg>
        <Tooltip ref={tooltipRef} />
      </Vizualisation>
    </>
  );
};

export default DotPlot;
